import {
  ALL_MODULES,
  AsyncPropSelector,
  DeleteModal,
  DropdownIndicator,
  Editor,
  IndicatorSeparator,
  Loader,
  TEXT_LENGTH,
  WorklogHoursSection,
  getTranslatedText,
  preFormatForm,
  statusStylesObject,
  taskConstraints,
  trimInputValues,
  validateForm,
  isAuthorised,
  useIsAuthorisedHook,
  getPriorityBadgeStatus
} from '@nile/components';
import DurationDropdown from '@nile/components/src/Dropdowns/DurationDropdown';
import {
  dompurifier,
  EventEmitter,
  getCurrentUserHook,
  getDateTimeFormat
} from '@nile/components/src/Utils/helpers';
import { AccountClient, GET_ALL_WORK_STATUSES, GET_TASK } from '@nile/graphql';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Card,
  DateTimePicker,
  Link,
  TextField as NumberInput,
  Page,
  RadioButton,
  Dropdown as Select,
  TextField,
  TextStyle,
  Checkbox
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import { cloneDeep, isEmpty } from 'lodash';
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { reactSelectErrorStyles, reactSelectInlineError } from '../Utils/style';
import { useQuery } from '@apollo/client';
import AddedWorklogs from '@nile/components/src/Containers/NewWorklog/AddedWorklogs.js';
import { GET_ALL_TECHNICIAN_GROUPS } from '@nile/graphql/src/Query/techgroup';
import { GET_COMPANY_USERS } from '@nile/graphql/src/Query/automationrules';
import { GET_ROLE_PERMISSON } from '@nile/banyan/src/Graphql/Mutations/role';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

const CustomRadioButton = ({ checked, onClick }) => {
  return (
    <RadioButton
      checked={checked}
      onClick={() => {
        if (onClick) onClick();
      }}
    />
  );
};

const TaskFromRevamp = (props) => {
  const {
    intl,
    toggleSheetActive,
    meta,
    data,
    module,
    hideSections = [],
    details,
    showParent,
    onDeleteHandler,
    ticketViewPermission,
    hideDelete,
    isTemplate,
    readOnly,
    workItem,
    isRunbookTemplate = false
  } = props;

  const taskId = props?.data?.taskId;

  const getWorkModule = () => {
    if (workItem?.module) {
      return workItem?.module;
    }
    if (props?.data?.project) {
      return 'PROJECT';
    }

    if (props?.data?.ticket) {
      return 'TICKET';
    }
    return null; // Default case
  };

  const workModule = getWorkModule();

  const context = useContext(TicketContext);
  const editorRef = useRef(null);
  const taskFromProps = props?.data;

  const TEMPLATE_DEFAULT_START_DATE = {
    unit: 'CUSTOM',
    value: 'TODAY'
  };

  const getDefaultTaskAndTemplate = () => {
    if (!taskId && !isEmpty(taskFromProps)) {
      const taskData = {
        name: taskFromProps?.title || '',
        description: taskFromProps?.description || '',
        status: taskFromProps?.status || 'Planned',
        estimatedTime: taskFromProps?.estimatedTime || 0,
        scheduledStartDate: taskFromProps?.scheduledStartDate,
        dueDate: taskFromProps?.dueDate || null,
        technician: !isEmpty(taskFromProps?.technician)
          ? {
              ...taskFromProps?.technician,
              name: `${taskFromProps?.technician?.firstName} ${taskFromProps?.technician?.lastName}`
            }
          : null,
        techGroup: !isEmpty(taskFromProps?.techGroup)
          ? taskFromProps?.techGroup
          : null,
        taskOrder: null,
        ...(!isTemplate && { priority: taskFromProps?.priority ?? null })
      };

      const templateData = {
        name: '',
        content: props?.data?.description || '',
        placeholdersUsed: '',
        inlineImages: []
      };

      return {
        taskData: taskData,
        templateData: templateData
      };
    }

    const defaultTask = {
      name: '',
      description: '',
      status: 'Planned',
      estimatedTime: 0,
      scheduledStartDate: props?.data?.scheduledStartDate,
      dueDate: null,
      technician: null,
      techGroup: null,
      taskOrder: null,
      ...(!isTemplate && { priority: props?.data?.priority ?? null })
    };

    const defaultTemplate = {
      name: '',
      content: '',
      placeholdersUsed: '',
      inlineImages: []
    };

    return {
      taskData: defaultTask,
      templateData: defaultTemplate
    };
  };

  const initialData = getDefaultTaskAndTemplate();

  const DEFAULT_TASK = initialData?.taskData;

  const DEFAULT_TEMPLATE = initialData?.templateData;

  const [task, setTask] = useState(DEFAULT_TASK);
  const [associatedWorkItem, setAssociatedWorkItem] = useState(
    data?.ticket || data?.project || null
  );
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [allTechnicians, setAllTechnicians] = useState(null);
  const [allTechGroups, setAllTechGroups] = useState(null);
  const [allWorkStatus, setAllWorkStatus] = useState(null);
  const [timerWorkItem, setTimerWorkItem] = useState(null);

  const [isTaskEditAuthorised, setIsTaskEditAuthorised] = useState(false);
  const [isTaskDeleteAuthorised, setIsTaskDeleteAuthorised] = useState(false);
  const [isWorkItemAuthorised, setIsWorkItemAuthorised] = useState(false);
  const [showWorklog, setShowWorklog] = useState(false);
  const isItProduct = useCheckIsItProduct();

  const getTimerWorkItem = (taskData) => {
    let defaultData = {
      module: taskData?.workItem?.module,
      workId: taskData?.workItem?.workId,
      client: taskData?.workItem?.client
    };

    if (taskData?.ticket) {
      defaultData = {
        ...defaultData,
        ticketId: taskData?.workItem?.workId,
        ticket: {
          ...taskData?.ticket,
          ...defaultData,
          contractTicketMapping:
            taskData?.workItem?.ticket?.contractTicketMapping
        }
      };
    }

    if (taskData?.project) {
      defaultData = {
        ...defaultData,
        projectId: taskData?.workItem?.workId,
        project: {
          ...taskData?.project,
          ...defaultData,
          contract: taskData?.workItem?.project?.contract
        }
      };
    }

    return defaultData;
  };

  const { loading: workStatusLoading } = useQuery(GET_ALL_WORK_STATUSES, {
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      if (res?.getWorkStatuses) {
        const workStatus = res?.getWorkStatuses.map((el) => ({
          ...el,
          id: el?.name,
          label: el?.name,
          value: el?.name
        }));
        setAllWorkStatus(workStatus);
      }
    }
  });

  const { loading: techGroupLoading } = useQuery(GET_ALL_TECHNICIAN_GROUPS, {
    fetchPolicy: 'no-cache',
    client: AccountClient,
    onCompleted: (res) => {
      if (res?.getTechnicianGroups) {
        const techGroups = res?.getTechnicianGroups.map((item) => ({
          ...item,
          id: item.name,
          value: item.name,
          label: item.name
        }));
        setAllTechGroups(techGroups);
      }
    }
  });

  const getCompanyUsersCondition = useMemo(() => {
    if (isItProduct) {
      return {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'roles.roleType.type',
            operator: 'includes',
            value: ['Technician']
          }
        ]
      };
    }
    return null;
  }, [isItProduct]);

  const { loading: techniciansLoading, refetch: refetchTechnicians } = useQuery(
    GET_COMPANY_USERS,
    {
      client: AccountClient,
      skip: taskId,
      variables: {
        listInfo: {
          page: 1,
          pageSize: 1000,
          sort: [
            {
              attribute: 'name',
              order: 'ASC'
            }
          ],
          condition: getCompanyUsersCondition
        }
      },
      fetchPolicy: 'no-cache',
      onCompleted: (res) => {
        if (res?.getCompanyUsersList?.userList) {
          const allTech = res.getCompanyUsersList.userList.map((item) => ({
            ...item,
            label: item.name,
            value: item.name
          }));
          setAllTechnicians(allTech);
        }
      }
    }
  );

  const isServiceCatalogAuthorised = useIsAuthorisedHook('serviceCatalog.view');
  const isTicketEditAuthorised = useIsAuthorisedHook('tickets.edit');
  const isProjectEditAuthorised = useIsAuthorisedHook(
    'projectManagementProject.edit'
  );

  const [roleLoading, setRoleLoading] = useState(false);

  const getProjectRoleFeatures = async (roleId) => {
    setRoleLoading(true);
    // Get roleId form project
    const response = await AccountClient.query({
      fetchPolicy: 'no-cache',
      query: GET_ROLE_PERMISSON,
      variables: { roleId: roleId }
    });
    const roleFeatureList = response?.data?.getRolePermissions?.roleFeatureList;
    // Check if user has add permission for project worklog
    // const isProjectWorklogAuthorised = isAuthorised(
    //   roleFeatureList,
    //   'projectWorklog.add'
    // );
    // projectRoleFeatures = roleFeatureList;
    setRoleLoading(false);
    return roleFeatureList;
  };

  const currentUser = getCurrentUserHook();
  const isTicketViewAuthorised = useIsAuthorisedHook('tickets.view');
  const isAllClientsAuthorised = useIsAuthorisedHook('allClients.allow');
  const isClientsAuthorised = useIsAuthorisedHook('client.view');
  const isAllProjectsAuthorised = useIsAuthorisedHook('allProjects.allow');

  const formatRoleFeatures = (roleFeatures) => {
    // Function to format role features
    if (Array.isArray(roleFeatures) && roleFeatures?.length > 0) {
      roleFeatures = roleFeatures.map((roleFeature) => {
        return {
          ...roleFeature,
          operationList: roleFeature?.operationList?.map((operation) => {
            return {
              ...operation,
              // Convert operationId to string
              operationId: operation?.operationId
                ? operation?.operationId.toString()
                : null
            };
          })
        };
      });
    }
    return roleFeatures;
  };

  const getTaskAuthorisation = async (workItemModule, taskData) => {
    const isTaskTechnician =
      taskData?.technician?.userId === currentUser?.userId;
    let roleFeatures = null;
    let worklogAuthorised = false;
    let editAuthorised = isTaskTechnician;
    let deleteAuthorised = false;
    let parentAuthorised = false;

    if (workItemModule === 'PROJECT') {
      const roleId = taskData?.project?.loggedInUserRole?.roleId;
      if (roleId) {
        // Get role features for project based on roleId
        roleFeatures = await getProjectRoleFeatures(roleId);
      }
      if (roleFeatures) {
        // Check if user has add permission for project worklog
        worklogAuthorised =
          isAuthorised(roleFeatures, 'projectWorklog.add') &&
          isServiceCatalogAuthorised;
        editAuthorised =
          isTaskTechnician ||
          isProjectEditAuthorised ||
          isAuthorised(roleFeatures, 'task.edit');
        deleteAuthorised =
          isProjectEditAuthorised || isAuthorised(roleFeatures, 'task.delete');
        parentAuthorised =
          isAllProjectsAuthorised || isAuthorised(roleFeatures, 'project.view');
      }
    }

    if (workItemModule === 'TICKET') {
      const roleFeaturesString =
        taskData?.ticket?.loggedInUserAsFollower?.ticketPermission ||
        taskData?.ticketPermission;
      worklogAuthorised = isServiceCatalogAuthorised && isTicketEditAuthorised;
      editAuthorised = isTaskTechnician || isTicketEditAuthorised;
      deleteAuthorised = isTicketEditAuthorised;

      if (!isEmpty(roleFeaturesString)) {
        roleFeatures = JSON.parse(roleFeaturesString);
        roleFeatures = formatRoleFeatures(roleFeatures);
        parentAuthorised = isAuthorised(roleFeatures, 'tickets.view');
      } else {
        parentAuthorised = isTicketViewAuthorised;
      }
    }

    return {
      worklogAuthorised,
      editAuthorised,
      deleteAuthorised,
      parentAuthorised
    };
  };

  const IT_DEFAULT_TECHNICIANS = useMemo(() => {
    return {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'roles.roleType.type',
          operator: 'includes',
          value: ['Technician']
        }
      ]
    };
  }, []);
  const [techsLoading, setTechsLoading] = useState(false);
  const getTechnicians = (groupId) => {
    let condition = null;
    if (groupId) {
      const operands = [
        {
          attribute: 'groups.groupId',
          operator: 'is',
          value: groupId
        }
      ];
      if (isItProduct) {
        operands.push(...IT_DEFAULT_TECHNICIANS.operands);
      }
      condition = {
        joinOperator: 'AND',
        operands
      };
    } else if (isItProduct) {
      condition = IT_DEFAULT_TECHNICIANS;
    }

    setTechsLoading(true);
    refetchTechnicians({
      listInfo: {
        page: 1,
        pageSize: 1000,
        sort: [{ attribute: 'name', order: 'ASC' }],
        condition
      }
    }).then((res) => {
      if (res?.data?.getCompanyUsersList?.userList) {
        const allTech = res.data.getCompanyUsersList.userList.map((item) => ({
          ...item,
          label: item.name,
          value: item.name
        }));
        setAllTechnicians(allTech);
        setTechsLoading(false);
      }
    });
  };

  const skipGetTask = !taskId || !workModule;
  const [loading, setLoading] = useState(!skipGetTask);

  function updateTemplate(obj) {
    if (obj) {
      setTemplate((prevState) => ({
        ...prevState,
        ...obj
      }));
    }
  }

  const { loading: taskLoading } = useQuery(GET_TASK, {
    variables: {
      taskId,
      module: workModule,
      isItProduct
    },
    fetchPolicy: 'no-cache',
    skip: skipGetTask,
    onCompleted: (res) => {
      if (res?.getTask) {
        // setLoading(true);
        const workItemData = getTimerWorkItem(res?.getTask);
        setTimerWorkItem(workItemData);

        // To check if worklog is authorised for ticket or project
        getTaskAuthorisation(workItemData?.module, res?.getTask).then(
          (response) => {
            const authorisation = response;
            setIsTaskEditAuthorised(authorisation?.editAuthorised);
            setIsTaskDeleteAuthorised(authorisation?.deleteAuthorised);
            setIsWorkItemAuthorised(authorisation?.parentAuthorised);
            setShowWorklog(authorisation?.worklogAuthorised);
          }
        );

        setTask({
          ...res?.getTask,
          name: res?.getTask?.title,
          scheduledStartDate: res?.getTask?.scheduledStartDate,
          dueDate: res?.getTask?.dueDate,
          priority: res?.getTask?.priority || null
        });
        if (res?.getTask?.workItem) {
          setAssociatedWorkItem(res?.getTask?.workItem);
        }
        getTechnicians(res?.getTask?.techGroup?.groupId);

        updateTemplate({
          content: dompurifier(res?.getTask?.description)
        });

        setLoading(false);
      }
    }
  });

  const [errors, setErrors] = useState(null);
  const [submitActionLoading, setSubmitActionLoading] = useState(false);

  const selectDateHandler = (val, type) => {
    const date = new Date(val);
    const formatted = val ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss') : null;
    const updatedTask = { ...task };
    updatedTask[type] = formatted;
    setTask(updatedTask);
  };

  const handleTemplateDateHandler = (val, type) => {
    const updatedTask = { ...task };
    updatedTask[type] = val;
    if (props?.isTemplate && isEmpty(updatedTask?.scheduledStartDate)) {
      updatedTask.scheduledStartDate = TEMPLATE_DEFAULT_START_DATE;
    }
    setTask(updatedTask);
  };

  const updateStatus = (statusObj) => {
    if (statusObj?.name) {
      const updatedTask = { ...task, status: statusObj?.name };
      setTask(updatedTask);
    }
  };

  const updatePriority = (field, priorityObj) => {
    const updatedTask = { ...task, priority: priorityObj?.value || null };
    setTask(updatedTask);
  };
  const CustomSingleValue = ({ data }) => (
    <Badge status={getPriorityBadgeStatus(data.value)}>{data.label}</Badge>
  );

  const handleNameChange = (val) => {
    const updatedName = val || '';
    setTask({
      ...task,
      name: updatedName
    });
  };

  const constructSelectObj = (obj) => {
    if (obj) {
      const { label, description, userList, clientList, value, ...rest } = obj;
      return rest;
    }
    return null;
  };

  const createTask = () => {
    setSubmitActionLoading(true);
    const dummyForm = { ...task, description: template.content };
    const validationResult = validateForm(
      preFormatForm(dummyForm),
      taskConstraints
    );
    if (validationResult) {
      setErrors(validationResult);
      setSubmitActionLoading(false);
      return;
    }
    setErrors(null);
    const scheduledStartDate =
      (props.isTemplate && task.scheduledStartDate?.value?.toString()) ||
      !props.isTemplate
        ? task?.scheduledStartDate || null
        : null;

    const dueDate =
      (props.isTemplate && task.dueDate?.value?.toString()) || !props.isTemplate
        ? task?.dueDate || null
        : null;

    let trimmedForm = {
      ...task,
      description: dompurifier(template.content),
      technician: constructSelectObj(task?.technician),
      techGroup: constructSelectObj(task?.techGroup),
      inlineImages: template?.inlineImages || [],
      scheduledStartDate,
      dueDate
    };
    trimmedForm = trimInputValues(trimmedForm);
    if (props?.addOrUpdateTechGroup) {
      props.addOrUpdateTechGroup(
        props.type,
        trimmedForm,
        setSubmitActionLoading
      );
    }
  };

  const getStatusObj = (name) => {
    if (allWorkStatus) {
      return allWorkStatus?.find(
        (status) => status.state === name || status.name === name
      );
    }
  };

  const updateSelectFields = (key, field) => {
    let updatedTask = cloneDeep(task);
    updatedTask = {
      ...updatedTask,
      [key]: field
    };
    if (key === 'techGroup') {
      getTechnicians(field?.groupId);
      updatedTask = {
        ...updatedTask,
        technician: null
      };
    } else if (key === 'technician') {
      setErrors((prevState) => ({
        ...prevState,
        technician: null
      }));
    }
    setTask(updatedTask);
  };

  const selectCustomUnit = () => {
    handleTemplateDateHandler(
      { unit: 'CUSTOM', value: 'TODAY' },
      'scheduledStartDate'
    );
  };

  const selectDaysUnit = () => {
    handleTemplateDateHandler(
      { unit: 'DAY', value: '0' },
      'scheduledStartDate'
    );
  };

  const redirectToWorkItem = () => {
    if (task?.workItem?.workId) {
      if (task?.project) {
        context.openTab({
          projectId: task?.workItem?.workId,
          module: ALL_MODULES.projectDetail,
          data: {
            projectId: task?.workItem?.workId,
            title: task?.project?.title
          }
        });
        toggleSheetActive();
        EventEmitter.dispatch('closeGlobalTimer');
      } else {
        context.openTab({
          ticketId: task?.workItem?.workId,
          module: 'TICKET',
          data: {
            subject: task?.ticket?.subject,
            displayId: task?.ticket?.displayId,
            ticketId: task?.workItem?.workId
          }
        });
        toggleSheetActive();
        EventEmitter.dispatch('closeGlobalTimer');
      }
    }
  };

  const handleEstimatedTime = (estTime) => {
    setTask((prevState) => ({
      ...prevState,
      estimatedTime: estTime
    }));
  };

  const handleAssociateWorkItem = (updatedModule, updatedWorkItem) => {
    setAssociatedWorkItem(updatedWorkItem);
  };

  // To show ticket or project in task
  // 1. Check for associated workItem
  // 2. Check for showParent from props
  let isDisabled = taskId ? !isTaskEditAuthorised || readOnly : false;
  if (!isDisabled && isRunbookTemplate) {
    // for runbook view only mode.
    isDisabled = isRunbookTemplate && readOnly;
  }

  const showAssociatedItem =
    !isEmpty(associatedWorkItem) && showParent && isWorkItemAuthorised;
  const showRightSection = showWorklog || showAssociatedItem;
  const showTaskDelete = !hideDelete && isTaskDeleteAuthorised;

  return (
    <Page
      title={`${meta.title}`}
      // ${props?.data?.taskTemplateId || ''}
      variant="secondary"
      divider
      contentHeight="82px"
      primaryAction={
        <ButtonGroup spacing="loose">
          {isRunbookTemplate && props.type === 'create' && (
            <Checkbox
              onChange={props.setSaveAsTemplate}
              checked={props.saveAsTemplate}
              label={'Save as Template'}
            />
          )}
          {isRunbookTemplate && !hideDelete && (
            <Button plainSecondary onClick={props.handleNodeDelete}>
              <div className="danger">
                {getTranslatedText(intl, 'common.delete')}
              </div>
            </Button>
          )}

          <Button
            plainSecondary
            onClick={() => {
              if (isRunbookTemplate && isEmpty(props.data)) {
                props.handleNodeDelete();
              }
              toggleSheetActive();
            }}
          >
            <FormattedMessage id="common.cancel" />
          </Button>
          {showTaskDelete && (
            <DeleteModal
              description={getTranslatedText(
                intl,
                'ticket.details.task.delete.confirm'
              )}
              handleDeleteConfirmation={onDeleteHandler}
              title={getTranslatedText(intl, 'ticket.details.task.delete.text')}
              zIndex={1500}
            />
          )}
          <Button
            primary
            icon={<SVGIcon name="Save" color="#fff" />}
            onClick={createTask}
            loading={submitActionLoading}
            disabled={isDisabled}
          >
            <FormattedMessage id="common.save" />
          </Button>
        </ButtonGroup>
      }
    >
      <div className="w-80vw vh80 mw1330" id="task-form-revamp-content-wrapper">
        {taskLoading || roleLoading || loading ? (
          <div className="flex actr jctr fhp full-width min-height-250">
            <Loader />
          </div>
        ) : (
          <div className="flex fx-sb fhp full-width ghost-white">
            <div
              className={
                showRightSection
                  ? 'w70 pl40 pt20 pr-20 pb20 bg-white fhp scroll'
                  : 'w100 pl40 pt20 pb20 pr40  bg-white fhp scroll'
              }
              id="task-data-container"
            >
              <div className="task-heading mb20">
                <TextField
                  placeholder={getTranslatedText(
                    intl,
                    'admin.runbooks.tasks.copy.name.pl'
                  )}
                  value={task?.name}
                  onChange={handleNameChange}
                  error={errors?.name?.[0]}
                  maxLength={TEXT_LENGTH.size_5}
                  disabled={isDisabled}
                  fontSize="fontLarge"
                />
              </div>

              <div className="flex g40">
                <div
                  id="left-container"
                  className={`w100 ${isDisabled && 'pointer-events-none'}`}
                >
                  {!hideSections.includes('techGroup') && (
                    <div className="mb-20" id="tech-group">
                      <div className="fx actr g10">
                        <div className="fx center">
                          <SVGIcon name="TechnicianGroup" />
                        </div>
                        <div className="f11">
                          <Select
                            placeholder={getTranslatedText(
                              intl,
                              'app.techGroupDropdown.placeholder'
                            )}
                            noBorder
                            isClearable
                            value={
                              task?.techGroup?.name
                                ? {
                                    name: task?.techGroup?.name,
                                    value: task?.techGroup?.name,
                                    label: task?.techGroup?.name
                                  }
                                : null
                            }
                            isLoading={techGroupLoading}
                            styles={
                              errors?.groupId
                                ? {
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    ...reactSelectErrorStyles
                                  }
                                : {
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    })
                                  }
                            }
                            components={{
                              IndicatorSeparator,
                              DropdownIndicator
                            }}
                            name="team"
                            onChange={(val) => {
                              updateSelectFields('techGroup', val);
                            }}
                            options={allTechGroups || []}
                            isDisabled={isDisabled}
                          />
                        </div>
                        {errors?.groupId &&
                          reactSelectInlineError(errors.groupId[0])}
                      </div>
                    </div>
                  )}

                  {isTemplate ? (
                    <>
                      {!hideSections.includes('scheduledStartDate') && (
                        <div className="mb-20" id="start-date-is-template">
                          <div className="fx actr" onClick={selectCustomUnit}>
                            <CustomRadioButton
                              checked={
                                task?.scheduledStartDate?.unit === 'CUSTOM'
                              }
                            />
                            <span className="ml-10 ptr">
                              <FormattedMessage id="admin.taskTemplate.startDate.creation" />
                            </span>
                          </div>
                          <div className="fx actr">
                            <CustomRadioButton
                              checked={task?.scheduledStartDate?.unit === 'DAY'}
                              onClick={selectDaysUnit}
                            />
                            <div className="flex actr ml-10">
                              <span className="ptr" onClick={selectDaysUnit}>
                                <FormattedMessage id="admin.taskTemplate.startDate.custom" />
                              </span>
                              &nbsp;
                              <NumberInput
                                value={task?.scheduledStartDate?.value}
                                suffix={getTranslatedText(
                                  intl,
                                  'admin.projectTemplate.startDate.custom.days'
                                )}
                                size="extrasmall"
                                type="number"
                                min={0}
                                onChange={(val) =>
                                  handleTemplateDateHandler(
                                    { unit: 'DAY', value: val },
                                    'scheduledStartDate'
                                  )
                                }
                                onBlur={(event) => {
                                  if (parseInt(event.target.value, 10) < 0) {
                                    handleTemplateDateHandler(
                                      { unit: 'DAY', value: '0' },
                                      'scheduledStartDate'
                                    );
                                  } else {
                                    const days = Math.round(
                                      parseInt(event.target.value, 10)
                                    );
                                    handleTemplateDateHandler(
                                      {
                                        unit: 'DAY',
                                        value: (Number.isNaN(days)
                                          ? ''
                                          : days
                                        )?.toString()
                                      },
                                      'scheduledStartDate'
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {!hideSections.includes('scheduledStartDate') && (
                        <div className="mb-20" id="taskStartDate">
                          <div className="fx actr g10">
                            <div>
                              <SVGIcon name="DatePicker" />
                            </div>
                            <div className="f11 task-date-time-picker position-relative">
                              <DateTimePicker
                                selected={
                                  task?.scheduledStartDate &&
                                  dayjs(task.scheduledStartDate).toDate()
                                }
                                onChange={(date) =>
                                  selectDateHandler(date, 'scheduledStartDate')
                                }
                                placeholderText={getTranslatedText(
                                  intl,
                                  'admin.runbooks.tasks.copy.startdate.pl'
                                )}
                                dateFormat={getDateTimeFormat().dateTimeFormat}
                                showTimeInput
                                disabled={isDisabled}
                                autoComplete="off"
                                aria-autocomplete="none"
                                customInput={
                                  <TextField
                                    placeholder={getTranslatedText(
                                      intl,
                                      'admin.runbooks.tasks.copy.startdate.pl'
                                    )}
                                    autoComplete="off"
                                    aria-autocomplete="none"
                                  />
                                }
                                portalId="root"
                                popperPlacement="bottom-start"
                              />
                              {!isEmpty(task?.scheduledStartDate) &&
                                !isDisabled && (
                                  <div
                                    className="position-absolute pointer task-datePicker-reset"
                                    onClick={() => {
                                      selectDateHandler(
                                        null,
                                        'scheduledStartDate'
                                      );
                                    }}
                                  >
                                    <SVGIcon name="Close" size="sm" />
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!hideSections.includes('estimatedTime') && (
                    <div className="mb-20" id="taskEstimatedTime">
                      <div className="fx actr g10">
                        <div>
                          <div className="fx center">
                            <SVGIcon name="EstimatedTime" />
                          </div>
                        </div>
                        <div className="w100" id="duration-dropdown">
                          <DurationDropdown
                            date={null}
                            duration={task?.estimatedTime || null}
                            endTime={null}
                            startTime={null}
                            onChange={handleEstimatedTime}
                            disabled={isDisabled}
                            placeholder={'Estimated time eg: "1h 3m"'}
                            maxDuration={360}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  id="right-container"
                  className={`w100 ${isDisabled && 'pointer-events-none'}`}
                >
                  {!hideSections.includes('technician') && (
                    <div className="mb-20" id="technician">
                      <div className="fx actr g10">
                        <Avatar
                          initials={
                            task?.technician?.name
                              ? task?.technician?.name?.[0]
                              : 'NA'
                          }
                          size="extraSmall"
                        />
                        <div className="f11">
                          <Select
                            noBorder
                            placeholder={getTranslatedText(
                              intl,
                              'common.choose.technician'
                            )}
                            value={
                              task?.technician?.name
                                ? {
                                    name: task?.technician?.name || null,
                                    value: task?.technician?.name || null,
                                    label: task?.technician?.name || null
                                  }
                                : null
                            }
                            isClearable
                            styles={
                              errors?.technician
                                ? {
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    ...reactSelectErrorStyles
                                  }
                                : {
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    })
                                  }
                            }
                            components={{
                              IndicatorSeparator,
                              DropdownIndicator
                            }}
                            name="team"
                            onChange={(val) => {
                              updateSelectFields('technician', val);
                            }}
                            isLoading={techniciansLoading || techsLoading}
                            options={allTechnicians || []}
                            isDisabled={isDisabled}
                          />
                          {errors?.technician &&
                            reactSelectInlineError(errors.technician)}
                        </div>
                      </div>
                    </div>
                  )}

                  {isTemplate ? (
                    <>
                      {!hideSections.includes('dueDate') && (
                        <div className="mb-20" id="task-end-date-template">
                          <div className="flex actr">
                            <FormattedMessage id="admin.projectTemplate.endDate.custom" />
                            &nbsp;
                            <NumberInput
                              value={task?.dueDate?.value}
                              suffix={getTranslatedText(
                                intl,
                                'admin.projectTemplate.endDate.custom.days'
                              )}
                              min={0}
                              onChange={(val) =>
                                handleTemplateDateHandler(
                                  { unit: 'DAY', value: val },
                                  'dueDate'
                                )
                              }
                              onBlur={(event) => {
                                if (parseInt(event.target.value, 10) < 0) {
                                  handleTemplateDateHandler(
                                    { unit: 'DAY', value: '0' },
                                    'dueDate'
                                  );
                                } else {
                                  const days = Math.round(
                                    parseInt(event.target.value, 10)
                                  );
                                  handleTemplateDateHandler(
                                    {
                                      unit: 'DAY',
                                      value: (Number.isNaN(days)
                                        ? ''
                                        : days
                                      )?.toString()
                                    },
                                    'dueDate'
                                  );
                                }
                              }}
                              size="extrasmall"
                              type="number"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {!hideSections.includes('dueDate') && (
                        <div className="mb-20" id="task-end-date">
                          <div className="fx actr g10">
                            <div>
                              <SVGIcon name="DatePicker" />
                            </div>
                            <div className="f11 task-date-time-picker position-relative">
                              <DateTimePicker
                                selected={
                                  task?.dueDate && dayjs(task.dueDate).toDate()
                                }
                                onChange={(date) =>
                                  selectDateHandler(date, 'dueDate')
                                }
                                placeholderText={getTranslatedText(
                                  intl,
                                  'admin.runbooks.tasks.copy.duedate.pl'
                                )}
                                dateFormat={getDateTimeFormat().dateTimeFormat}
                                showTimeInput
                                disabled={isDisabled}
                                autoComplete="off"
                                aria-autocomplete="none"
                                customInput={
                                  <TextField
                                    placeholder={getTranslatedText(
                                      intl,
                                      'admin.runbooks.tasks.copy.duedate.pl'
                                    )}
                                    autoComplete="off"
                                    aria-autocomplete="none"
                                  />
                                }
                                portalId="root"
                                popperPlacement="bottom-start"
                              />

                              {!isEmpty(task?.dueDate) && !readOnly && (
                                <div
                                  className="position-absolute pointer task-datePicker-reset"
                                  onClick={() => {
                                    selectDateHandler(null, 'dueDate');
                                  }}
                                >
                                  <SVGIcon name="Close" size="sm" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!hideSections.includes('status') && (
                    <div className="mb-20" id="tech-group">
                      <div className="fx actr g10">
                        <div className="fx center">
                          <SVGIcon name="Status" />
                        </div>
                        <div className="f11">
                          <Select
                            noBorder
                            value={getStatusObj(task.status)}
                            styles={
                              errors?.statusId
                                ? {
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    ...reactSelectErrorStyles,
                                    ...statusStylesObject
                                  }
                                : {
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999
                                    }),
                                    ...statusStylesObject
                                  }
                            }
                            isDisabled={isDisabled}
                            name="team"
                            components={{
                              IndicatorSeparator,
                              DropdownIndicator
                            }}
                            isLoading={workStatusLoading}
                            onChange={updateStatus}
                            options={allWorkStatus || []}
                            isSearchable={false}
                          />
                        </div>
                        {errors?.statusId &&
                          reactSelectInlineError(errors.statusId[0])}
                      </div>
                    </div>
                  )}

                  {!hideSections.includes('priority') && !isTemplate && (
                    <div className="mb-20" id="task-priority">
                      <div className="fx actr g10">
                        <div className="fx center">
                          <SVGIcon name="Flag" />
                        </div>
                        <div className="f11">
                          <AsyncPropSelector
                            field={{ name: 'priority' }}
                            entity="Task"
                            defaultValue={
                              task?.priority
                                ? {
                                    value: task.priority,
                                    label: task.priority
                                  }
                                : null
                            }
                            value={
                              task?.priority
                                ? {
                                    value: task.priority,
                                    label: task.priority
                                  }
                                : null
                            }
                            updateSelection={updatePriority}
                            isClearable
                            isDisabled={isDisabled}
                            hideTitle
                            placeholder="Select Priority"
                            customComponents={{
                              SingleValue: CustomSingleValue
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div
                id="task-editor"
                className={`card-border pt10 pr20 pl20 br10 task-form-editor ch-230 ${isDisabled &&
                  'disabled opacity5 pointer-events-none'}`}
              >
                <Editor
                  focusEditor
                  template={template}
                  updateTemplate={updateTemplate}
                  module={module || 'TICKET'}
                  details={details || { operation: 'CREATE' }}
                  placeholder={getTranslatedText(
                    intl,
                    'admin.runbooks.tasks.copy.pl.desc'
                  )}
                  allowMultiple
                  config={{
                    htmlUntouched: true
                  }}
                  disableAttachment
                  toolbarSticky={false}
                  ref={editorRef}
                />
              </div>
            </div>

            {showRightSection && (
              <div
                id="worklog-and-workitem"
                className="maxW400 fw p20 ghost-white fhp scroll"
              >
                {showParent && showAssociatedItem && (
                  <div className="full-width mb-20" id="associate-item-card">
                    <Card customClassName="full-width" padding="sizeSmall">
                      <div className="flex actr fx-sb mb-10">
                        <div>
                          <TextStyle weight="SemiBold">
                            <FormattedMessage id="common.associated.entity" />
                          </TextStyle>
                        </div>
                      </div>

                      <div>
                        <div className="fx fx-sb">
                          <div className="flex actr g5">
                            <div className="flex">
                              <SVGIcon
                                name={
                                  task?.project
                                    ? 'ProjectFilled'
                                    : 'TicketFilled'
                                }
                              />
                            </div>
                            <div className="fx actr">
                              <Link
                                removeUnderline
                                onClick={redirectToWorkItem}
                              >
                                {associatedWorkItem?.subject ||
                                  associatedWorkItem?.title ||
                                  'Click here'}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                )}

                {!isEmpty(taskId) && !isEmpty(timerWorkItem) && showWorklog && (
                  <>
                    {!isDisabled && (
                      <WorklogHoursSection
                        module={timerWorkItem?.module}
                        workItem={{ ...timerWorkItem }}
                        ticket={
                          timerWorkItem?.module === 'TICKET'
                            ? timerWorkItem?.ticket
                            : null
                        }
                        project={
                          timerWorkItem?.module === 'PROJECT'
                            ? timerWorkItem?.project
                            : null
                        }
                        refetch={() => {}}
                        allowEmptyNotes={false}
                        onViewModeClick={() => {}}
                        hideCancel
                        showAlltimersList
                        highlightRunningTimer
                        taskId={taskId}
                        hideWorkitem
                        reAssociateWorkItem={false}
                        hideWorklogTask
                        editWorklogZIndex={518}
                        hideBlockItemConsumptionInfo={
                          isAllClientsAuthorised && isClientsAuthorised
                        }
                      />
                    )}

                    <div className="mt10">
                      <AddedWorklogs
                        workItem={timerWorkItem}
                        ticket={
                          timerWorkItem?.module === 'TICKET'
                            ? timerWorkItem
                            : null
                        }
                        project={
                          timerWorkItem?.module === 'PROJECT'
                            ? timerWorkItem
                            : null
                        }
                        module={timerWorkItem?.module}
                        refetch={() => {}}
                        taskId={taskId}
                        hideTask
                        removeTaskBorder
                        editTippyZIndex={518}
                        hideAI
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  );
};

export default injectIntl(TaskFromRevamp);
